import type { ReactNode } from 'react'
import { Box, Chip, InputAdornment, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import { useTranslation } from 'react-i18next'

import { splitByGaps } from '../contentTypes'
import type { DropdownGapsContent, GapsContent, SentenceBuilderContent } from '../contentTypes'
import { DragPickerProvider } from './dnd'
import { sourceProps, targetProps, useDragPicker } from './dragPicker'
import { VerdictMark } from './VerdictMark'
import {
  VERDICT_BORDER_WIDTH,
  useVerdictTitle,
  verdictAccent,
  verdictBorderStyle,
  verdictFrameSx,
  verdictIcon,
  verdictTint,
  type VerdictOutcome,
} from './verdictStyles'

/**
 * Рендереры типов с пропусками. Ответ собирается в форму, которую ждёт чекер:
 * `{ gaps: { "1": "…" }, hints: ["1"] }` — ключ пропуска и есть номер
 * плейсхолдера в тексте.
 */

export type RendererProps<T> = {
  content: T
  answers: Record<string, unknown>
  onChange: (answers: Record<string, unknown>) => void
  readOnly?: boolean
  /**
   * Разбор автопроверки по элементам: пропускам, вариантам, парам.
   *
   * Считает его сервер и присылает в `check_details` — на клиенте сравнивать
   * нечем, эталоны у решающего вырезаны. Приходит только там, где работу уже
   * проверили и смотрит её тот, кому эталоны положены.
   */
  check?: Record<string, unknown> | null
}

function gapsOf(answers: Record<string, unknown>): Record<string, string> {
  const value = answers.gaps
  return typeof value === 'object' && value !== null ? (value as Record<string, string>) : {}
}

/**
 * Вердикты автопроверки по пропускам: номер пропуска → отвечено верно или нет.
 *
 * Читаем только у закрытой работы: у решающего ученика разбора нет вовсе, а
 * покажи мы его на активной попытке — подсветка выдала бы ответ. Карту с
 * числовыми ключами сервер сериализует то объектом, то массивом, поэтому
 * обходим пары; всё, что не похоже на `{ ok: boolean }`, остаётся без вердикта
 * — «ни то ни сё» врёт сильнее, чем отсутствие подсветки.
 */
function gapVerdicts(
  check: Record<string, unknown> | null | undefined,
  readOnly: boolean | undefined,
): Record<string, boolean> {
  if (readOnly !== true) return {}

  const raw = check?.gaps
  if (typeof raw !== 'object' || raw === null) return {}

  const verdicts: Record<string, boolean> = {}

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const ok = (value as { ok?: unknown } | null | undefined)?.ok
    if (typeof ok === 'boolean') verdicts[key] = ok
  }

  return verdicts
}

/**
 * Исход одного пропуска: решено, ошибка или пропущено.
 *
 * Чекер пустому пропуску ставит `ok: false` — для балла это верно, ноль он и
 * есть ноль. Но читателю «ошибка» и «не отвечал» говорят разное: первое значит
 * «пробовал и не смог», второе — «не дошёл или не знал». Красить их одинаково
 * значит приписывать ученику ошибки, которых он не делал.
 */
type GapOutcome = VerdictOutcome | undefined

function gapOutcome(ok: boolean | undefined, answer: string | undefined): GapOutcome {
  if (ok === undefined) return undefined
  if (ok) return 'correct'

  return (answer ?? '').trim() === '' ? 'skipped' : 'wrong'
}

/**
 * Эталон пропуска — из содержимого задания.
 *
 * У `cloze` это список принятых вариантов, у `dropdown_gaps` — индекс верного
 * в списке. В разборе автопроверки эталона нет вовсе: она отвечает «сошлось
 * или нет», а не «а как было надо».
 */
function clozeReference(content: GapsContent, key: string): string | null {
  const gaps = (content as unknown as { gaps?: unknown }).gaps

  if (typeof gaps !== 'object' || gaps === null) return null

  const value = (gaps as Record<string, unknown>)[key]

  if (Array.isArray(value)) {
    const first = value.find((row): row is string => typeof row === 'string' && row.trim() !== '')

    return first ?? null
  }

  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function dropdownReference(gap: { options?: string[]; correct?: number } | undefined): string | null {
  if (gap === undefined || typeof gap.correct !== 'number') return null

  return gap.options?.[gap.correct] ?? null
}

/**
 * Верный ответ рядом с неверным.
 *
 * Одного «неверно» мало: учитель смотрит разбор, чтобы понять, чего ученик не
 * знает, а для этого нужно видеть обе стороны — что написал и что было надо.
 */
function GapReference({ value }: { value: string | null }) {
  const { t } = useTranslation()

  if (value === null) return null

  // Без приставки «верно»: у каждого пропуска она забивала строку словом
  // вместо ответа. Эталон отмечает галочка, а сам текст — основного цвета:
  // зелёный текст под полем читался на 2.5:1. Слово остаётся в title.
  return (
    <Typography
      component="span"
      variant="caption"
      title={t('exercises.run.checkReference')}
      // Тот же отступ, что у поля (`mx: 0.5`): без него подпись прижималась к
      // левому краю обёртки и висела левее самого пропуска.
      sx={{
        ml: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        color: 'text.primary',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      }}
    >
      <VerdictMark outcome="correct" fontSize="inherit" />
      {value}
    </Typography>
  )
}

/**
 * Пропуск с эталоном под ним.
 *
 * Эталон стоял справа от поля и разрывал предложение: между «The bridge ___» и
 * «in 1890» вклинивалась подпись, и текст задания читать было нельзя. Под
 * полем он не мешает строке и очевидно относится именно к этому пропуску.
 */
function GapWithReference({
  reference,
  children,
}: {
  reference: string | null
  children: ReactNode
}) {
  if (reference === null) {
    return <>{children}</>
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        // По базовой линии текста, а не по середине: иначе поле съезжает вниз
        // вместе с подписью и выпадает из строки.
        verticalAlign: 'middle',
      }}
    >
      {children}
      <GapReference value={reference} />
    </Box>
  )
}

/**
 * Стили проверенного поля: корень `InputBase` — сам `Select` или поле внутри
 * `TextField` (туда стили уходят через `slotProps.input`).
 *
 * Без вердикта возвращаем пустой объект: рендер решения не должен отличаться от
 * прежнего ни на пиксель. Проверенная работа показана в выключенных полях, и
 * каждое правило ниже написано и для `.Mui-disabled`: тема (EA-598) красит
 * подложку, рамку и текст выключенного поля своими селекторами с этим классом,
 * и без такой же специфичности вердикт им проигрывал — зелёный текст оставался
 * на сером.
 */
function fieldVerdictStyles(theme: Theme, outcome: GapOutcome): SystemStyleObject<Theme> {
  if (outcome === undefined) return {}

  const text = theme.palette.text.primary
  const border = {
    borderWidth: VERDICT_BORDER_WIDTH,
    borderStyle: verdictBorderStyle(outcome),
    borderColor: verdictAccent(theme, outcome),
  }

  return {
    borderRadius: 1,
    '&, &.Mui-disabled': { backgroundColor: verdictTint(theme, outcome) },
    // Ответ — основным цветом: MUI красит выключенный текст через
    // `-webkit-text-fill-color`, одного `color` тут мало
    '& .MuiInputBase-input, & .MuiInputBase-input.Mui-disabled': {
      color: text,
      WebkitTextFillColor: text,
    },
    // Выпадающий список: рамка поля — это `notchedOutline`
    '& .MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline, &.Mui-disabled:hover .MuiOutlinedInput-notchedOutline':
      border,
    // Поле ввода с подчёркиванием: подчёркивание меняем на ту же рамку, что у
    // списков, — иначе проверенный пропуск выглядит по-разному в соседних типах
    '&.MuiInput-root': {
      ...border,
      px: 0.75,
      // У маленького поля MUI отступы несимметричные (1px сверху, 5px снизу):
      // под подчёркиванием это незаметно, в рамке текст съезжал вверх
      '& .MuiInputBase-input': { py: '3px' },
    },
    '&.MuiInput-root::before, &.MuiInput-root::after': { display: 'none' },
    '& .MuiSelect-icon, & .MuiSelect-icon.Mui-disabled': { color: verdictAccent(theme, outcome) },
  }
}

/** Пропуск drag-and-drop: вместо поля вставленное слово, рамка та же */
function gapVerdictStyles(theme: Theme, outcome: VerdictOutcome) {
  return { ...verdictFrameSx(theme, outcome), borderRadius: 1, gap: 0.5 }
}

export function ClozeRenderer({ content, answers, onChange, readOnly, check }: RendererProps<GapsContent>) {
  const gaps = gapsOf(answers)
  const verdicts = gapVerdicts(check, readOnly)
  const verdictTitle = useVerdictTitle()

  return (
    <Typography component="div" sx={{ lineHeight: 2.4 }}>
      {splitByGaps(content.text).map((part, index) => {
        if (part.kind === 'text') return <span key={index}>{part.value}</span>

        const key = String(part.index)
        const ok = verdicts[key]
        const outcome = gapOutcome(ok, gaps[key])

        return (
          <GapWithReference
            key={index}
            reference={
              outcome !== undefined && outcome !== 'correct' ? clozeReference(content, key) : null
            }
          >
            <TextField
              size="small"
              variant="standard"
              title={verdictTitle(outcome)}
              sx={{ mx: 0.5, width: 140 }}
              slotProps={{
                input: {
                  sx: (theme: Theme) => fieldVerdictStyles(theme, outcome),
                  endAdornment:
                    outcome === undefined ? undefined : (
                      <InputAdornment position="end">
                        <VerdictMark outcome={outcome} />
                      </InputAdornment>
                    ),
                },
              }}
              value={gaps[key] ?? ''}
              disabled={readOnly}
              onChange={(event) => onChange({ ...answers, gaps: { ...gaps, [key]: event.target.value } })}
            />
          </GapWithReference>
        )
      })}
    </Typography>
  )
}

function DragGapsBody({ content, answers, onChange, readOnly, check }: RendererProps<GapsContent>) {
  const { t } = useTranslation()
  const picker = useDragPicker()
  const gaps = gapsOf(answers)
  const used = Object.values(gaps)
  const verdicts = gapVerdicts(check, readOnly)
  const verdictTitle = useVerdictTitle()

  const place = (gapKey: string, word: string) => {
    onChange({ ...answers, gaps: { ...gaps, [gapKey]: word } })
  }

  return (
    <Stack spacing={3}>
      <Typography component="div" sx={{ lineHeight: 2.6 }}>
        {splitByGaps(content.text).map((part, index) => {
          if (part.kind === 'text') return <span key={index}>{part.value}</span>

          const key = String(part.index)
          const value = gaps[key]
          const ok = verdicts[key]
          const outcome = gapOutcome(ok, value)

          return (
            <Box
              key={index}
              component="span"
              title={verdictTitle(outcome)}
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 110,
                mx: 0.5,
                px: 1,
                py: 0.5,
                cursor: readOnly ? 'default' : 'pointer',
                // На проверенной работе вместо подчёркивания — рамка вердикта:
                // «заполненный» primary и пустой divider ей только мешали бы
                ...(outcome === undefined
                  ? { borderBottom: 2, borderColor: value ? 'primary.main' : 'divider' }
                  : gapVerdictStyles(theme, outcome)),
              })}
              {...targetProps((word) => place(key, word), picker, readOnly)}
              onDoubleClick={() => {
                if (readOnly) return
                const next = { ...gaps }
                delete next[key]
                onChange({ ...answers, gaps: next })
              }}
            >
              {value || ' '}
              {outcome !== undefined && <VerdictMark outcome={outcome} />}
            </Box>
          )
        })}
      </Typography>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('exercises.run.wordBank')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(content.bank ?? []).map((word, index) => (
            <Chip
              key={`${word}-${index}`}
              label={word}
              color={picker.isPicked(word) ? 'primary' : 'default'}
              variant={used.includes(word) ? 'outlined' : 'filled'}
              disabled={readOnly}
              {...sourceProps(word, picker, readOnly)}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('exercises.run.dragHint')}
        </Typography>
      </Box>
    </Stack>
  )
}

export function DragGapsRenderer(props: RendererProps<GapsContent>) {
  return (
    <DragPickerProvider>
      <DragGapsBody {...props} />
    </DragPickerProvider>
  )
}

export function DropdownGapsRenderer({
  content,
  answers,
  onChange,
  readOnly,
  check,
}: RendererProps<DropdownGapsContent>) {
  const gaps = gapsOf(answers)
  const verdicts = gapVerdicts(check, readOnly)
  const verdictTitle = useVerdictTitle()

  return (
    <Typography component="div" sx={{ lineHeight: 2.6 }}>
      {splitByGaps(content.text).map((part, index) => {
        if (part.kind === 'text') return <span key={index}>{part.value}</span>

        const key = String(part.index)
        // Позиция пропуска в тексте и есть его позиция в списке gaps
        const gap = content.gaps[part.index - 1]
        const ok = verdicts[key]

        const outcome = gapOutcome(ok, gaps[key])

        return (
          <GapWithReference
            key={index}
            reference={outcome !== undefined && outcome !== 'correct' ? dropdownReference(gap) : null}
          >
            <Select
              size="small"
              displayEmpty
              title={verdictTitle(outcome)}
              // Проверенный список не открыть, и стрелка обещала бы лишнее: на
              // её месте — значок вердикта
              IconComponent={outcome === undefined ? undefined : verdictIcon(outcome)}
              sx={(theme) => ({ mx: 0.5, minWidth: 140, ...fieldVerdictStyles(theme, outcome) })}
              value={gaps[key] ?? ''}
              disabled={readOnly}
              onChange={(event) => onChange({ ...answers, gaps: { ...gaps, [key]: String(event.target.value) } })}
            >
              <MenuItem value="">—</MenuItem>
              {(gap?.options ?? []).map((option, optionIndex) => (
                <MenuItem key={optionIndex} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </GapWithReference>
        )
      })}
    </Typography>
  )
}

function SentenceBuilderBody({ content, answers, onChange, readOnly }: RendererProps<SentenceBuilderContent>) {
  const { t } = useTranslation()
  const order = Array.isArray(answers.order) ? (answers.order as number[]) : []

  const append = (index: number) => {
    if (order.includes(index)) return
    onChange({ ...answers, order: [...order, index] })
  }

  const removeAt = (position: number) => {
    onChange({ ...answers, order: order.filter((_, i) => i !== position) })
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          minHeight: 64,
          p: 2,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {order.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('exercises.run.buildHere')}
            </Typography>
          )}
          {order.map((wordIndex, position) => (
            <Chip
              key={`${wordIndex}-${position}`}
              label={content.words[wordIndex]}
              color="primary"
              disabled={readOnly}
              onDelete={readOnly ? undefined : () => removeAt(position)}
            />
          ))}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {content.words.map((word, index) => (
          <Chip
            key={`${word}-${index}`}
            label={word}
            variant={order.includes(index) ? 'outlined' : 'filled'}
            disabled={readOnly || order.includes(index)}
            onClick={() => append(index)}
          />
        ))}
      </Stack>
    </Stack>
  )
}

export function SentenceBuilderRenderer(props: RendererProps<SentenceBuilderContent>) {
  return (
    <DragPickerProvider>
      <SentenceBuilderBody {...props} />
    </DragPickerProvider>
  )
}
